import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, Warning, Receipt, User, CalendarBlank } from "@phosphor-icons/react";

/**
 * Iter 238 · Duplicate-bill guardrail modal.
 *
 * Fetches `/finance/invoices/nearby` for the given customer_id +
 * service_date (± window_days) and, if any matches exist, renders a
 * warning modal with two actions:
 *
 *  - "OK, Close"                → cancels invoice creation
 *  - "Create new invoice anyway" → tells the parent to continue with
 *                                  duplicate-bill flow.
 *
 * If NO nearby invoices exist, the modal auto-invokes `onProceed` and
 * silently unmounts — the caller sees no interruption.
 *
 * Props:
 *  - customerId  · required
 *  - serviceDate · YYYY-MM-DD · required
 *  - windowDays  · default 2
 *  - onProceed   · called when user chooses to create anyway (or no dupes exist)
 *  - onCancel    · called when user chooses "OK, Close"
 */
export default function NearbyInvoicesModal({
  customerId, serviceDate, windowDays = 2, onProceed, onCancel,
}) {
  const [state, setState] = useState({ loading: true, rows: [] });

  useEffect(() => {
    let cancelled = false;
    if (!customerId || !serviceDate) {
      setState({ loading: false, rows: [] });
      return;
    }
    const qs = new URLSearchParams({
      customer_id: customerId,
      service_date: serviceDate,
      window_days: String(windowDays),
    }).toString();
    api.get(`/finance/invoices/nearby?${qs}`).then(r => {
      if (cancelled) return;
      const rows = r.data?.rows || [];
      if (rows.length === 0) {
        onProceed?.({ silent: true, matches: [] });
      } else {
        setState({ loading: false, rows });
      }
    }).catch(e => {
      if (cancelled) return;
      // On error, don't block invoice creation — just log and proceed.
      toast.error(formatApiError(e));
      onProceed?.({ silent: true, matches: [], error: true });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, serviceDate, windowDays]);

  if (state.loading) return null;
  const { rows } = state;
  if (!rows.length) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
           data-testid="nearby-invoices-modal">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[92vh] overflow-auto shadow-xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#E5E2D9] bg-[#FEF3C7] flex items-start gap-3 sticky top-0 z-10">
          <Warning size={28} weight="fill" className="text-[#B45309] flex-shrink-0 mt-0.5"/>
          <div className="flex-1">
            <div className="font-heading text-xl text-[#7A2E18]">
              Existing invoice{rows.length > 1 ? "s" : ""} for this customer
            </div>
            <div className="text-[13px] text-[#7A2E18]/80 mt-0.5">
              We found {rows.length} bill{rows.length > 1 ? "s" : ""} within ±{windowDays} day
              {windowDays === 1 ? "" : "s"} of {serviceDate}.
              Please review before creating a duplicate.
            </div>
          </div>
          <button onClick={onCancel} data-testid="nearby-close-x"
                    className="p-1 hover:bg-black/5 rounded"><X size={18}/></button>
        </div>

        {/* Rows */}
        <div className="p-6 space-y-3">
          {rows.map(r => (
            <NearbyRow key={r.id} r={r}/>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#E5E2D9] bg-[#F7F6F2] flex items-center justify-end gap-2 sticky bottom-0">
          <div className="text-[11px] text-[#8A8780] mr-auto">
            {rows.filter(r => r.same_day).length > 0
              ? "⚠ At least one existing bill is on the SAME service date."
              : "No same-day match — nearby invoices only."}
          </div>
          <button onClick={onCancel} data-testid="nearby-ok-close"
                    className="px-4 h-10 rounded border border-[#E5E2D9] text-[#5C5A55] text-sm hover:bg-white">
            OK, Close
          </button>
          <button onClick={() => onProceed?.({ silent: false, matches: rows })}
                    data-testid="nearby-create-anyway"
                    className="px-4 h-10 rounded bg-[#B45309] hover:bg-[#92400E] text-white text-sm inline-flex items-center gap-2">
            <Receipt size={14}/> Create new invoice anyway
          </button>
        </div>
      </div>
    </div>
  );
}

function NearbyRow({ r }) {
  const paid = (r.payment_status || "").toLowerCase() === "paid";
  return (
    <div className={`border rounded-md p-4 ${r.same_day
        ? "border-[#B45309] bg-[#FFFBEB]"
        : "border-[#E5E2D9] bg-white"}`}
         data-testid={`nearby-row-${r.id}`}>
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-semibold">{r.invoice_no || "—"}</span>
            {r.same_day ? (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#B45309] text-white">
                Same day
              </span>
            ) : (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#E5E2D9] text-[#5C5A55]">
                {r.days_from_target > 0 ? "+" : ""}{r.days_from_target}d
              </span>
            )}
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
              paid ? "bg-[#D4E0D1] text-[#1A3317]"
                    : "bg-[#F9E4B7] text-[#7A5A11]"}`}>
              {r.payment_status || r.status || "—"}
            </span>
          </div>
          <div className="mt-1.5 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-[12px] text-[#5C5A55]">
            <div className="flex items-center gap-1.5">
              <CalendarBlank size={12} className="text-[#8A8780]"/>
              <span>Service:</span>
              <span className="font-mono text-[#1A1A1A]">
                {r.service_date || (r.created_at || "").slice(0, 10)}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <User size={12} className="text-[#8A8780]"/>
              <span>Field staff:</span>
              <span>{r.field_staff_name || "—"}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <User size={12} className="text-[#8A8780]"/>
              <span>Billed by:</span>
              <span>{r.billed_by_name || "—"}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Receipt size={12} className="text-[#8A8780]"/>
              <span>Package:</span>
              <span>{r.package_name || "—"}</span>
            </div>
          </div>
          {r.package_lines && (
            <div className="mt-2 text-[11px] text-[#5C5A55] italic border-t border-[#F1EFE7] pt-2">
              {r.package_lines}
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading text-lg text-[#1A1A1A]">
            ₹{Number(r.total || 0).toFixed(2)}
          </div>
          {r.gst_amount ? (
            <div className="text-[10px] text-[#8A8780]">
              incl. ₹{Number(r.gst_amount).toFixed(2)} GST
            </div>
          ) : null}
          {paid && r.payment_method && (
            <div className="text-[10px] text-[#1A3317] mt-1">
              via {r.payment_method}
              {r.paid_at && ` · ${r.paid_at.slice(0, 10)}`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
